import { locationIdFormat } from "@redotech/redo-ids-cockroach";
import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";

/**
 * Lists HANDED_OFF pick sessions waiting at a given zone+location for the
 * caller's team. Drives the pickup prompt on the start-new-session page:
 * the picker selects/scans handoffs to consolidate into a fresh wave they
 * are about to start.
 *
 * Per-handoff payload includes the totes that arrived from the upstream
 * picker so the UI can render them for scroll-selection and so a tote
 * barcode scan can resolve to its session.
 */
export const listAvailableHandoffsAtZone = {
  name: "listAvailableHandoffsAtZone" as const,
  input: z.object({ locationId: locationIdFormat.schema, zoneId: z.string() }),
  output: z.object({
    // Count of "stranded" no-tote handoffs at this zone. These don't
    // appear in `handoffs[]` (nothing physical to scan/grab so the
    // pickup modal would be useless), but the picker still needs SOME
    // signal that there's work waiting — otherwise the start-new-session
    // cards stay disabled and the server-side auto-consolidate path
    // never gets triggered. The UI uses this count to (a) enable the
    // strategy cards even when fresh-order count is 0, and (b) show a
    // hint like "N stranded item(s) at your zone — click Start to clear".
    strandedHandoffCount: z.number(),
    handoffs: z.array(
      z.object({
        pickSessionId: zExt.objectId(),
        fulfillmentGroupId: zExt.objectId(),
        // Count of distinct PickSessionItem entries. Kept for backwards
        // compat / debug; the picker UI uses the two qty fields below
        // because they reflect physical reality and remaining work.
        itemCount: z.number(),
        // Total units already in the handoff's totes (sum of
        // quantityPicked across all items). Tells the picker how much
        // physical stock they're about to carry.
        pickedItemQty: z.number(),
        // Total units the picker will be responsible for picking after
        // claiming, scoped to the requested zone. Sum of
        // (totalQuantity − quantityPicked − quantityOutOfStock) over
        // items where zoneId === zoneId argument. Reflects additional
        // work, not the carried-forward upstream picks.
        remainingPickQtyAtZone: z.number(),
        totes: z.array(
          z.object({
            toteId: zExt.objectId(),
            name: z.string(),
            barcode: z.string(),
          }),
        ),
      }),
    ),
  }),
};
